import { baseService } from "src/store/base.store";
import { OnboardingReport } from "../types/onboarding-report.entity";
import { onboardingReportRepository } from "../repositories/onboarding-report.repository";
import { executeQuery } from "src/store";
import { FilterQuery } from "src/firebase/firestore/types";

export interface OnboardingReportsResponse {
  reports: OnboardingReport[];
  total: number;
}

export interface OnboardingReportsFilter {
  userId?: string;
  screenName?: string;
  status?: string;
  email?: string;
  platform?: string;
}

export const buildOnboardingReportWhere = async (
  where: OnboardingReportsFilter
): Promise<FilterQuery<OnboardingReport>["where"]> => {
  const q: any = {};

  if (where.userId) {
    q["userId"] = where.userId;
  }

  if (where.screenName) {
    q["screenName"] = where.screenName;
  }

  if (where.status) {
    q["status"] = where.status;
  }

  if (where.email) {
    q["email"] = where.email;
  }

  if (where.platform) {
    q["platform"] = where.platform;
  }

  return q;
};

export const onboardingReportService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getOnboardingReports: builder.query<
      OnboardingReport[],
      { where?: OnboardingReportsFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where, offset, limit }) => {
        try {
          const filterQuery = await buildOnboardingReportWhere(where || {});
          const pageSize = typeof limit === "number" && limit > 0 ? limit : 20;

          const queryOptions: any = {
            where: filterQuery,
            limit: pageSize,
            orderBy: ["createdAt", "desc"],
          };

          if (offset) {
            queryOptions.offset = offset;
          }

          const reports = await onboardingReportRepository.find(queryOptions);
          return { data: reports };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch onboarding reports",
            },
          };
        }
      },
      providesTags: ["onboarding-reports"],
    }),
    countOnboardingReports: builder.query<number, { where?: OnboardingReportsFilter }>({
      queryFn: async ({ where }) => {
        try {
          const filterQuery = await buildOnboardingReportWhere(where || {});
          const total = await onboardingReportRepository.count(filterQuery);
          return { data: total };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to count onboarding reports",
            },
          };
        }
      },
      providesTags: ["onboarding-reports"],
    }),
    getOnboardingReportById: builder.query<OnboardingReport, string>({
      queryFn: async (id) => {
        return executeQuery(() => onboardingReportRepository.findById(id));
      },
      keepUnusedDataFor: 600,
    }),
    updateOnboardingReportStatus: builder.mutation<
      void,
      { reportId: string; status: string }
    >({
      queryFn: async ({ reportId, status }) => {
        try {
          await onboardingReportRepository.updateStatus(reportId, status as any);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to update report status",
            },
          };
        }
      },
      invalidatesTags: ["onboarding-reports"],
    }),
  }),
});

export const {
  useGetOnboardingReportsQuery,
  useCountOnboardingReportsQuery,
  useGetOnboardingReportByIdQuery,
  useUpdateOnboardingReportStatusMutation,
} = onboardingReportService;
